import { useMutation, useQuery } from '@tanstack/react-query';
import { Col, Divider, Form, Row, TabsProps, TimePicker } from 'antd';
import dayjs from 'dayjs';
import { useEffect, useState } from 'react';
import { useIntl } from 'react-intl';
import { generalConfigApi, recruitmentTypeApi, reminderTimeApi } from '../../../../apis';
import { CreateReminderTimeDto } from '../../../../apis/client-axios';
import ButtonWrap from '../../../../components/ButtonWrap';
import ToastError from '../../../../components/Toast/ToastError';
import ToastSuccess from '../../../../components/Toast/ToastSuccess';
import Widget from '../../../../components/Widget';
import { TIME_FORMAT_WITH_HOURS_MINUTES } from '../../../../constants/common';
import { QUERY_KEY } from '../../../../constants/QueryKey';
import './style.scss';
import SelectWrap from '../../../../components/SelectWrap';
import { DefaultOptionType } from 'antd/es/select';

const n = (key: keyof CreateReminderTimeDto) => key;

const TimekeepingReminder = () => {
  const intl = useIntl();

  const [form] = Form.useForm();

  const [recruitmentTypeId, setRecruitmentTypeId] = useState<string>();

  const { data: recruitmentTypesResponse } = useQuery({
    queryKey: [QUERY_KEY.USER_TYPE_LIST],
    queryFn: () => {
      return recruitmentTypeApi.recruitmentTypeControllerGetAll(1, undefined, undefined, undefined, undefined);
    },
  });

  const { data: reminderTimeResponse, refetch } = useQuery({
    queryKey: [QUERY_KEY.TIMEKEEPING_REMINDER, recruitmentTypeId],
    queryFn: () => {
      return reminderTimeApi.reminderTimeControllerFindAll();
    },
  });

  const { data: lineGroupResponse } = useQuery({
    queryKey: [QUERY_KEY.LINE_GROUP_LIST],
    queryFn: () => {
      return generalConfigApi.generalConfigControllerGetLineGroups();
    },
  });

  const upsertReminderTimeMutation = useMutation({
    mutationFn: (payload: CreateReminderTimeDto) => reminderTimeApi.reminderTimeControllerSave(payload),
    onSuccess: () => {
      refetch();
      ToastSuccess({
        content: intl.formatMessage({ id: 'common.success' }),
      });
    },
    onError: () => {
      refetch();
      ToastError({
        content: intl.formatMessage({ id: 'common.errorOccurred' }),
      });
    },
  });

  const items: TabsProps['items'] = Array.isArray(recruitmentTypesResponse?.data?.data?.content)
    ? recruitmentTypesResponse?.data?.data?.content?.map((item) => ({
        key: item.id,
        label: item.type,
      }))
    : [];

  const lineGroupsOptions =
    lineGroupResponse?.data?.data?.map(
      (lg) =>
        ({
          label: lg.groupName,
          value: lg.id,
        }) as DefaultOptionType,
    ) ?? [];

  useEffect(() => {
    if (
      Array.isArray(recruitmentTypesResponse?.data?.data?.content) &&
      recruitmentTypesResponse?.data?.data?.content?.length &&
      !recruitmentTypeId
    ) {
      setRecruitmentTypeId(recruitmentTypesResponse?.data?.data?.content?.[0].id);
    }
  }, [recruitmentTypesResponse?.data?.data?.content, recruitmentTypeId]);

  useEffect(() => {
    if (reminderTimeResponse?.data?.data) {
      const reminderTime = reminderTimeResponse?.data?.data?.find(
        (reminderTime) => reminderTime.recruitmentTypeId === recruitmentTypeId,
      );
      form.setFieldsValue({
        [n('checkInTime')]: reminderTime?.checkInTime
          ? dayjs(reminderTime?.checkInTime, TIME_FORMAT_WITH_HOURS_MINUTES)
          : null,
        [n('checkOutTime')]: reminderTime?.checkOutTime
          ? dayjs(reminderTime?.checkOutTime, TIME_FORMAT_WITH_HOURS_MINUTES)
          : null,
        [n('checkOutTime2')]: reminderTime?.checkOutTime2
          ? dayjs(reminderTime?.checkOutTime2, TIME_FORMAT_WITH_HOURS_MINUTES)
          : null,
        [n('summaryTime')]: reminderTime?.summaryTime
          ? dayjs(reminderTime?.summaryTime, TIME_FORMAT_WITH_HOURS_MINUTES)
          : null,
        [n('summaryTime2')]: reminderTime?.summaryTime2
          ? dayjs(reminderTime?.summaryTime2, TIME_FORMAT_WITH_HOURS_MINUTES)
          : null,
        [n('lineGroupId')]: reminderTime?.lineGroupId,
      });
    }
  }, [reminderTimeResponse?.data?.data, recruitmentTypeId]);

  const onFinish = (formValues: CreateReminderTimeDto) => {
    console.log(formValues);
    upsertReminderTimeMutation.mutate({
      checkInTime: formValues.checkInTime ? dayjs(formValues.checkInTime).format(TIME_FORMAT_WITH_HOURS_MINUTES) : '',
      checkOutTime: formValues.checkOutTime
        ? dayjs(formValues.checkOutTime).format(TIME_FORMAT_WITH_HOURS_MINUTES)
        : '',
      checkOutTime2: formValues.checkOutTime2
        ? dayjs(formValues.checkOutTime2).format(TIME_FORMAT_WITH_HOURS_MINUTES)
        : '',
      recruitmentTypeId: recruitmentTypeId as string,
      summaryTime: formValues.summaryTime ? dayjs(formValues.summaryTime).format(TIME_FORMAT_WITH_HOURS_MINUTES) : '',
      summaryTime2: formValues.summaryTime2
        ? dayjs(formValues.summaryTime2).format(TIME_FORMAT_WITH_HOURS_MINUTES)
        : '',
      lineGroupId: formValues.lineGroupId as string,
    });
  };

  return (
    <>
      <Widget
        breadcrumbs={[intl.formatMessage({ id: 'timekeepingReminder.breadcrumbs' })]}
        tabProps={{
          items,
          onChange: (key: string) => setRecruitmentTypeId(key),
        }}
        content={
          <Form form={form} onFinish={onFinish}>
            <Row gutter={[16, 16]} className="timekeeping-reminder-content">
              <Col span={24} className="timekeeping-reminder-content-header">
                {intl.formatMessage({ id: 'timekeepingReminder.header' })}
              </Col>
              <Col span={24}>
                <Row gutter={[16, 16]} align="middle">
                  <Col>
                    <Form.Item name={n('checkInTime')} className="mb-0">
                      <TimePicker
                        format={TIME_FORMAT_WITH_HOURS_MINUTES}
                        use12Hours={false}
                        needConfirm={false}
                        showNow={false}
                        className="width-96 height-40 border-color-dcdcdc border-radius-1"
                        placeholder=""
                      />
                    </Form.Item>
                  </Col>

                  <Col className="timekeeping-reminder-content-label">
                    {intl.formatMessage({ id: 'timekeepingReminder.checkInTime' })}
                  </Col>
                </Row>
              </Col>

              <Col span={24}>
                <Row gutter={[16, 16]} align="middle">
                  <Col>
                    <Form.Item name={n('checkOutTime')} className="mb-0">
                      <TimePicker
                        format={TIME_FORMAT_WITH_HOURS_MINUTES}
                        use12Hours={false}
                        needConfirm={false}
                        showNow={false}
                        className="width-96 height-40 border-color-dcdcdc border-radius-1"
                        placeholder=""
                      />
                    </Form.Item>
                  </Col>

                  <Col className="timekeeping-reminder-content-label">
                    {intl.formatMessage({ id: 'timekeepingReminder.checkOutTime' })}
                  </Col>
                </Row>
              </Col>

              <Col span={24}>
                <Row gutter={[16, 16]} align="middle">
                  <Col>
                    <Form.Item name={n('checkOutTime2')} className="mb-0">
                      <TimePicker
                        format={TIME_FORMAT_WITH_HOURS_MINUTES}
                        use12Hours={false}
                        needConfirm={false}
                        showNow={false}
                        className="width-96 height-40 border-color-dcdcdc border-radius-1"
                        placeholder=""
                      />
                    </Form.Item>
                  </Col>

                  <Col className="timekeeping-reminder-content-label">
                    {intl.formatMessage({ id: 'timekeepingReminder.checkOutTime2' })}
                  </Col>
                </Row>
              </Col>

              <Col span={24}>
                <Divider className="form-divider m-1" />
              </Col>

              <Col span={24}>
                <Row gutter={[16, 16]} align="middle">
                  <Col>
                    <Form.Item name={'summaryTime'} className="mb-0">
                      <TimePicker
                        format={TIME_FORMAT_WITH_HOURS_MINUTES}
                        use12Hours={false}
                        needConfirm={false}
                        showNow={false}
                        className="width-96 height-40 border-color-dcdcdc border-radius-1"
                        placeholder=""
                      />
                    </Form.Item>
                  </Col>

                  <Col className="timekeeping-reminder-content-label">
                    {intl.formatMessage({ id: 'timekeepingReminder.summaryTime' })}
                  </Col>

                  <Col span={12}>
                    <Form.Item name={'lineGroupId'} className="mb-0 ml-2">
                      <SelectWrap
                        className="width-200 height-40 border-color-dcdcdc border-radius-1 reminder-select"
                        placeholder={intl.formatMessage({ id: 'timekeepingReminder.lineGroup' })}
                        options={lineGroupsOptions}
                        allowClear
                      />
                    </Form.Item>
                  </Col>
                </Row>
              </Col>

              <Col span={24}>
                <Row gutter={[16, 16]} align="middle">
                  <Col>
                    <Form.Item name={'summaryTime2'} className="mb-0">
                      <TimePicker
                        format={TIME_FORMAT_WITH_HOURS_MINUTES}
                        use12Hours={false}
                        needConfirm={false}
                        showNow={false}
                        className="width-96 height-40 border-color-dcdcdc border-radius-1"
                        placeholder=""
                      />
                    </Form.Item>
                  </Col>

                  <Col className="timekeeping-reminder-content-label">
                    {intl.formatMessage({ id: 'timekeepingReminder.summaryTime2' })}
                  </Col>
                </Row>
              </Col>
            </Row>
            <ButtonWrap htmlType="submit" width={162} height={48} className="ms-auto">
              {intl.formatMessage({ id: 'common.save' })}
            </ButtonWrap>
          </Form>
        }
        title={intl.formatMessage({ id: 'timekeepingReminder.title' })}
      />
    </>
  );
};

export default TimekeepingReminder;
